import {
  DrawerDescription,
  TooltipContent,
  TooltipTrigger,
  FormDatePicker,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  Typography,
  FormSelect,
  FormInput,
  Tooltip,
  Drawer,
  Button,
  Label,
  Form,
} from '@ui/components';
import { ColorOptions } from '@pages/employees-profile/components';
import { Cancel, Delete, Info } from '@ui/components/icons';
import { CompanyPolicyResponse } from '@interfaces/models';
import { HasPermission } from '@shared/components';
import { useHasPermission } from '@shared/hooks';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@shared/hooks';
import { Masks } from '@helpers/constant';
import { DateTime } from 'luxon';
import { FC } from 'react';

import { useEditEmployeesProfile } from './use-edit-employees-profile';

interface EditEmployeesProfileDrawerProps {
  data: CompanyPolicyResponse;
  onClose: ({ handleSuccess }: { handleSuccess: boolean }) => void;
  onRemove: () => void;
  isOpen: boolean;
}

export const EditEmployeesProfileDrawer: FC<EditEmployeesProfileDrawerProps> = ({
  onRemove,
  onClose,
  isOpen,
  data,
}) => {
  const { currency } = useCurrentUser();
  const { t, i18n } = useTranslation();

  const { isPending, onSubmit, form } = useEditEmployeesProfile({
    onSuccess: () => onClose({ handleSuccess: true }),
    id: data.id,
  });

  const hasEditPermission = useHasPermission('EMPLOYEES_PROFILE_EDIT_PROFILE', 'WRITE');
  const validFrom = form.watch('validFrom');
  const validTo = form.watch('validTo');

  return (
    <Drawer onClose={() => onClose({ handleSuccess: false })} open={isOpen} handleOnly>
      <DrawerContent className="flex flex-col gap-0 overflow-hidden p-0" draggable="false">
        <DrawerHeader className="flex flex-row items-center justify-between p-4 border-b border-border-weak">
          <DrawerTitle asChild>
            <Typography variant="h200">
              {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.EDIT_DRAWER_HEADER')}
            </Typography>
          </DrawerTitle>
          <DrawerDescription />

          <div className="flex flex-row gap-2">
            <HasPermission
              permissionFeature="EMPLOYEES_PROFILE_EDIT_PROFILE"
              permissionType="WRITE"
            >
              {!data.isDefault && (
                <Button onClick={onRemove} variant="ghost" size="small" icon>
                  <Delete />
                </Button>
              )}
            </HasPermission>
            <DrawerClose asChild>
              <Button variant="ghost" size="small" icon>
                <Cancel />
              </Button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        <form className="flex-1 overflow-auto flex flex-col p-4 pt-5 gap-2" onSubmit={onSubmit}>
          <div className="flex-1">
            <div className="space-y-4">
              <Form {...form}>
                <div className="grid grid-cols-6 gap-2 items-center border-b border-border-weak pb-4">
                  <Label className="col-span-2" htmlFor="name">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.PROFILE_NAME')}
                    </Typography>
                  </Label>
                  <div className="col-span-4 flex items-center">
                    <FormInput
                      componentProps={{ id: 'name' }}
                      className="w-full col-span-4"
                      disabled={!hasEditPermission}
                      formControl={form.control}
                      size="small"
                      name="name"
                      required
                    />
                  </div>

                  <Label className="col-span-2" htmlFor="color">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.COLOR')}
                    </Typography>
                  </Label>

                  <div className="col-span-4 flex items-center">
                    <FormSelect
                      className="w-full col-span-4"
                      disabled={!hasEditPermission}
                      formControl={form.control}
                      options={ColorOptions()}
                      size="small"
                      name="color"
                    />
                  </div>

                  <Label className="col-span-2" htmlFor="validFrom">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.VALID_FROM')}
                    </Typography>
                  </Label>

                  <div className="col-span-4 flex items-center gap-2">
                    <FormDatePicker
                      disabled={
                        data.isDefault ||
                        !hasEditPermission || {
                          before: new Date(),
                          after: validTo
                            ? DateTime.fromJSDate(validTo)
                                .minus({ days: 1 })
                                .endOf('day')
                                .toJSDate()
                            : undefined,
                        }
                      }
                      componentProps={{ id: 'validFrom', lang: i18n.language }}
                      className="w-full col-span-4"
                      formControl={form.control}
                      name="validFrom"
                      size="small"
                    />

                    <Button
                      onClick={() => form.setValue('validFrom', null)}
                      disabled={!validFrom || !hasEditPermission}
                      variant="ghost"
                      type="button"
                      icon
                    >
                      <Cancel />
                    </Button>
                  </div>

                  <Label className="col-span-2" htmlFor="validTo">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.VALID_TO')}
                    </Typography>
                  </Label>

                  <div className="col-span-4 flex items-center gap-2">
                    <FormDatePicker
                      disabled={
                        data.isDefault ||
                        !hasEditPermission || {
                          before: DateTime.fromJSDate(validFrom || new Date())
                            .plus({ days: 1 })
                            .startOf('day')
                            .toJSDate(),
                        }
                      }
                      componentProps={{ id: 'validTo', lang: i18n.language }}
                      className="w-full col-span-4"
                      formControl={form.control}
                      name="validTo"
                      size="small"
                    />

                    <Button
                      onClick={() => form.setValue('validTo', null)}
                      disabled={!validTo || !hasEditPermission}
                      variant="ghost"
                      type="button"
                      icon
                    >
                      <Cancel />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-6 gap-2 items-center border-b border-border-weak pb-4">
                  <div className="col-span-6 flex gap-2 items-center">
                    <Typography variant="labelMediumBold" element="span">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.MAX_AMOUNT')}
                    </Typography>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="text-weak" />
                      </TooltipTrigger>
                      <TooltipContent alignOffset={50} side="bottom">
                        {t('EMPLOYEE_PROFILES.MAX_AMOUNT_TOOLTIP')}
                      </TooltipContent>
                    </Tooltip>
                  </div>

                  <Label className="col-span-2" htmlFor="maxAmount">
                    <Typography variant="labelSmallBook" className="text-weak" element="label">
                      {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.AMOUNT')}
                    </Typography>
                  </Label>

                  <div className="col-span-4 flex items-center">
                    <FormInput
                      trailingIcon={{
                        position: 'right',
                        icon: (
                          <Typography className="text-content-weak" variant="labelSmallBold">
                            {currency}
                          </Typography>
                        ),
                      }}
                      componentProps={{
                        id: 'maxAmount',
                        maskOptions: { mask: Masks.CURRENCY_FORMAT },
                      }}
                      disabled={!hasEditPermission}
                      className="w-full col-span-4"
                      formControl={form.control}
                      name="maxAmount"
                      size="small"
                    />
                  </div>
                </div>
              </Form>
            </div>
          </div>
          <Button
            disabled={isPending || !hasEditPermission}
            isLoading={isPending}
            className="shrink-0"
            type="submit"
            size="large"
            wide
          >
            {t('EMPLOYEE_PROFILES.MANAGE_EMPLOYEE_PROFILE.EDIT_PROFILE_SUBMIT')}
          </Button>
        </form>
      </DrawerContent>
    </Drawer>
  );
};
